import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "../../environments/environment";
import { Constants } from "../../constants";
import { User } from "../../models/user";
import { Company } from "../../models/company";
import { Router } from "@angular/router";
import { UtilsService } from "../utils";
import { UserGroup } from "../../models/userGroup";
import { Subject } from "rxjs";
import {CompanyDocument} from "../../models/CompanyDocument";
import {CompanyDocumentService} from "../companyDocument/document.service";

@Injectable({
    providedIn: 'root'
})
export class UserService {

    /** Emits when the JWT has been refreshed (e.g. after payment). Components like navbar can subscribe to refresh their state. */
    readonly jwtRefreshed$ = new Subject<void>();

    constructor(private http: HttpClient, private router: Router, @Inject(PLATFORM_ID) private platformId: any, private utils: UtilsService,public companyDocumentService: CompanyDocumentService, ) {
    }

    getRequestHeaders() {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': localStorage.getItem(Constants.LOCAL_TOKEN) ?? environment.publicApiToken
        });
    }

    getPublicRequestHeaders() {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': environment.publicApiToken,
        });
    }
    getPublicRequestHeadersForUpload() {
        return new HttpHeaders({
            'Authorization': environment.publicApiToken,
        });
    }

    getFormDataRequestHeaders() {
        return new HttpHeaders({
            Authorization: localStorage.getItem(Constants.LOCAL_TOKEN) ?? environment.publicApiToken
        });
    }

    async register(user: User) {
        console.log('nel register: ', user)
        try {
            let response: any = await this.http.post(environment.authApiUrl + '/signupUser', user, {
                headers: this.getPublicRequestHeaders(),
            }).toPromise();

            console.log('response signup: ', response)
            return response;
        } catch (e) {
            console.error('error creating user: ', e)
        }
    }

    async login(email: string, password: string) {
        let response: any;
        try {
            response = await this.http.post(environment.authApiUrl + '/login', { email, password }, {
                headers: this.getPublicRequestHeaders(),
            }).toPromise();

            // if ( !response.success ) return response;

            this.utils.loginSuccess(response);
        } catch (e) {
            console.error('error creating user: ', e)
        } finally {
            return response
        }
    }

    async loginAs(email: string, userId: string) {
        let response: any;
        try {
            response = await this.http.post(environment.apiUrl + `/users/${userId}/loginAs`, {}, {
                headers: this.getRequestHeaders(),
            }).toPromise();

            // if ( !response.success ) return response;

            this.utils.loginSuccess(response);
        } catch (e) {
            console.error('error creating user: ', e)
        } finally {
            return response
        }
    }

    async getUserOverview(userId: string) {
        let response: any;
        try {
            response = await this.http.get(environment.apiUrl + `/users/${userId}/overview`, {
                headers: this.getRequestHeaders(),
            }).toPromise();
        } catch (e) {
            console.error('error getUserOverview: ', e);
        } finally {
            return response;
        }
    }

    // Admins skip the old-password check server side
    async adminSetPassword(userId: string, newPassword: string) {
        let response: any;
        try {
            response = await this.http.put(environment.apiUrl + `/users/${userId}/password`, {oldPassword: '', newPassword}, {
                headers: this.getRequestHeaders(),
            }).toPromise();
        } catch (e) {
            console.error('error adminSetPassword: ', e);
        } finally {
            return response;
        }
    }

    async deleteUser(userId: string) {
        return await this.http.delete(environment.apiUrl + `/users/${userId}`, {
            headers: this.getRequestHeaders(),
        }).toPromise();
    }

    async recoverPassword(emailToSendNotification: string) {
        let response: any;

        try {
            response = await this.http.post(environment.authApiUrl + '/recover', { email: emailToSendNotification }, { headers: this.getPublicRequestHeaders() }).toPromise();
        } catch (e) {
            console.error('error recoverPassword')
        } finally {
            return response;
        }
    }

    async resetPassword(email: string, password: string, recoveryNumber: string) {
        let response: any;

        try {
            response = await this.http.post(environment.authApiUrl + '/reset', {
                email,
                password,
                recoveryNumber
            }, { headers: this.getPublicRequestHeaders() }).toPromise();
        } catch (e) {
            console.error('error resetPassword: ', e)
        } finally {
            return response;
        }
    }

    async getUserById(id: string): Promise<User> {
        try {
            const response: any = await this.http.get(environment.apiUrl + `/users/${id}`, {
                headers: this.getRequestHeaders(),
            }).toPromise();

            if (response?.success && response.data) {
                return new User(response.data);
            }
        } catch (e) {
            console.error('error getAllAthletes: ', e)
        }
        return new User();
    }

    async getUsers(params: Record<string, string | number> = {}): Promise<{ success?: boolean; data?: any[]; count?: number }> {
        try {
            return await this.http.get<{ success?: boolean; data?: any[]; count?: number }>(
                environment.apiUrl + '/users',
                { params, headers: this.getRequestHeaders() }
            ).toPromise() as { success?: boolean; data?: any[]; count?: number };
        } catch (e) {
            console.error('error getUsers: ', e);
            return { success: false, data: [], count: 0 };
        }
    }

    async getCurrentUser() {
        let response: any;
        // let user: User = new User();

        try {
            response = await this.http.get(environment.apiUrl + `/users/me`, {
                headers: this.getRequestHeaders(),
            }).toPromise()

            /*            if (response.success) {
                            user = response.data;
                        }*/

            console.log('currentUSer: ', response)
        } catch (e) {
            console.error('error usersMe: ', e)
        } finally {
            return response;
        }
    }

    async createNewUser(newUser: User) {
        let response: any;

        try {
            response = await this.http.post(environment.apiUrl + '/users', newUser, { headers: this.getRequestHeaders() }).toPromise();
        } catch (e) {
            console.error('error createNewUser: ', e)
        } finally {
            return response;
        }
    }

    async athleteOnboarding(user: User) {
        let response: any;

        try {
            response = await this.http.post(environment.apiUrl + '/users/athleteOnboarding', user, {
                headers: this.getRequestHeaders()
            }).toPromise();
        } catch (e) {
            console.error('error athleteOnboarding: ', e);
        } finally {
            return response;
        }
    }

    async updatePassword(newPassword: string, userId: string) {

        let response: any;

        try {
            response = await this.http.put(environment.apiUrl + `/users/${userId}`,
                { password: newPassword },
                { headers: this.getRequestHeaders() }).toPromise();
        } catch (e) {
            console.error('error updatePassword: ', e)
        } finally {
            return response.success
        }
    }

    async updateUser(updatedUser: User) {
        let response: any;

        try {
            response = await this.http.put(environment.apiUrl + `/users/${updatedUser.id}`, updatedUser, { headers: this.getRequestHeaders() }).toPromise()
        } catch (e) {
            console.error('error updateUser: ', e)
        } finally {
            return response.success;
        }
    }

    async uploadUser(formData: FormData) {
        let response: any;

        try {
            response = await this.http.post(environment.publicApiUrl + '/users/upload', formData, { headers: this.getPublicRequestHeadersForUpload() }).toPromise();
        } catch (e) {
            console.error('error uploadUser: ', e)
        } finally {
            return response;
        }
    }

    async uploadUserAuthenticated(formData: FormData) {
        let response: any;

        try {
            response = await this.http.post(environment.apiUrl + '/users/upload', formData, {
                headers: this.getFormDataRequestHeaders()
            }).toPromise();
        } catch (e) {
            console.error('error uploadUserAuthenticated: ', e);
        } finally {
            return response;
        }
    }

    logout() {
        localStorage.clear();
        this.router.navigate(['login'])
            .then(_ => window.location.reload())
    }

    //Agenti
    async registerAgent(agent: User) {
        console.log('register agent: ', agent)

        let agentToRegister =
        {
            "firstName": agent.firstName,
            "lastName": agent.lastName,
            "businessName": agent.businessName,
            "fiscalCode": agent.fiscalCode,
            "vat": agent.vat,
            "pec": agent.pec,
            "sdi": agent.sdi,
            "email": agent.email,
            "phone": agent.phone,
            "password": agent.password,
            "address": agent.address,
            "addressNumber": agent.addressNumber,
            "city": agent.city,
            "zip": agent.zip,
            "province": agent.province,
            "documentNumber": agent.documentNumber,
            "documentUrlFront": agent.documentUrlFront,
            "documentUrlBack": agent.documentUrlBack,
            "bank": agent.bank,
            "iban": agent.iban,
            "accountHolderPerson": agent.accountHolderPerson,
            "accountHolderCompany": agent.accountHolderCompany,
            "paymentPhone": agent.paymentPhone,
            "paymentEmail": agent.paymentEmail,
            "acceptTerms": new Date().toISOString(),
            "acceptPrivacy": new Date().toISOString(),
            "subtype": agent.subtype
        };

        try {
            let response: any = await this.http.post(environment.authApiUrl + '/signupAgent', agentToRegister, {
                headers: this.getPublicRequestHeaders(),
            }).toPromise();

            return response;
        } catch (e) {
            console.error('error creating user: ', e)
        }
    }

    async getAllAgents(additionalFilter = {}) {
        let agents: any = [];

        let filter = { userGroupId: 'agent' }
        if (Object.keys(additionalFilter).length > 0) filter = { ...filter, ...additionalFilter }

        console.log('filter service: ', filter);
        try {
            let response: any = await this.http.get(environment.apiUrl + '/users', {
                headers: this.getRequestHeaders(),
                params: additionalFilter
            }).toPromise()

            if (response.success) {
                agents = [...response.data];
            }

        } catch (e) {
            console.error('error getAllAgents: ', e)
        } finally {
            return agents;
        }
    }

    async getAgent(agentId: string): Promise<User> {
        try {
            const response: any = await this.http.get(environment.apiUrl + `/users/${agentId}`, {
                headers: this.getRequestHeaders(),
            }).toPromise();

            if (response?.success && response.data) {
                return new User(response.data);
            }
        } catch (e) {
            console.error('error getAgent: ', e)
        }
        return new User();
    }

    async resendOTP(email: string) {
        try {
            return await this.http.post(environment.authApiUrl + '/confirmOtp', {
                email
            }, { headers: this.getPublicRequestHeaders() }).toPromise();
        } catch (e: any) {
            console.error('error checkOTP: ', e);
            return e?.error ?? { success: false, code: 'USER_NOT_FOUND' };
        }
    }

    async checkOTP(otp: string, email: string) {
        let response: any;

        try {
            response = await this.http.post(environment.authApiUrl + '/verify', {
                otp,
                email
            }, { headers: this.getPublicRequestHeaders() }).toPromise();
        } catch (e) {
            console.error('error checkOTP: ', e)
        } finally {
            return response;
        }
    }

    async searchUsers(text: string, companyId: string, top?: number) {
        let user: User[] = [];
        try {
            let response: any = await this.http.get(environment.apiUrl + `/users`, {
                params: top ? { text, companyId, $top: top } : { text, companyId },
                headers: this.getRequestHeaders(),
            }).toPromise()

            if (response.success) {
                user = response.data;
            }

        } catch (e) {
            console.error('error getAllAthletes: ', e)
        } finally {
            return user;
        }
    }
    async typeOfAccount() {
        let userGroups: UserGroup[] = []
        try {
            let response: any = await this.http.get(environment.apiUrl + `/userGroups`, {
                headers: this.getRequestHeaders(),
            }).toPromise();

            if (response.success) {
                userGroups = response.data;
            }

        } catch (e) {
            console.error('error getAllAthletes: ', e)
        } finally {
            return userGroups;
        }
    }

    async checkExistingUser(params: any) {
        let response: any;
        try {
            response = await this.http.get(environment.publicApiUrl + `/checkExistingUser`, {
                params,
                headers: this.getPublicRequestHeaders(),
            }).toPromise();
        } catch (e) {
            console.error('error getAllAthletes: ', e)
        } finally {
            return response;
        }
    }

    /**
     * Refresh JWT using stored refresh token.
     * Expected response body:
     *   { userId, refreshToken, accessToken }
     */
    async refreshJwtFromRefreshToken(redirectToDashboard: boolean = false) {
        const storedRefreshToken = localStorage.getItem('wisper_refreshToken');
        const userId = localStorage.getItem('wisper_userId');
        if (!storedRefreshToken || !userId) {
            return;
        }

        let response: any;
        try {
            response = await this.http.post(environment.authApiUrl + '/refresh', {
                refreshToken: storedRefreshToken,
                userId: userId,
            }, {
                headers: this.getPublicRequestHeaders(),
            }).toPromise();

            if (response && response.success && response.data) {
                localStorage.setItem(Constants.LOCAL_TOKEN, response.data.accessToken);
                if (response.data.refreshToken) {
                    localStorage.setItem('wisper_refreshToken', response.data.refreshToken);
                    localStorage.setItem(Constants.LOCAL_TOKEN, response.data.accessToken);
                }
                if (response.data.onboarding) {
                    localStorage.setItem('register-company-onboarding-token', response.data.onboarding.resumeToken);
                    localStorage.setItem('register-company-step', String(response.data.onboarding.currentStep ?? 1));
                }

                this.jwtRefreshed$.next();

                // Optionally redirect back to the proper dashboard after a silent refresh.
                if (redirectToDashboard) {
                    if (response.data.onboarding) {
                        this.router.navigate(['/register/companies']);
                        return response;
                    }
                    const group = this.utils.getJwtUserGroup();
                    if (group === 'admin') {
                        this.router.navigate(['/admins/dashboard']);
                    } else if (group === 'companymanager') {
                        this.router.navigate(this.utils.getCompanyManagerHomeRoute());
                    } else if (group === 'user') {
                        this.router.navigate(['/users/dashboard']);
                    } else if (group === 'agent') {
                        this.router.navigate(['/agents/dashboard']);
                    } else if (group === 'mister') {
                        this.router.navigate(['/misters/dashboard']);
                    }
                }
            }
        } catch (e) {
            console.error('error refreshJwtFromRefreshToken: ', e);
        }
    }


    async checkUserMandatoryDocuments(userId: string, companyId: string) {
        const listOfMandatoryDocuments: CompanyDocument[] = (await this.companyDocumentService
            .getUserCompanyDocuments(companyId, userId))
            .filter((document: CompanyDocument) => document.isMandatory);

        let userCompanyDocuments: CompanyDocument[] | null = null;
        try {
            const userDocumentsResponse = await this.getUserDocuments(userId);
            userCompanyDocuments = userDocumentsResponse.data;
        } catch (error) {
            // Gestisci l'errore se necessario o lascia userCompanyDocuments come null
        }

        let missingCompanyDocuments: CompanyDocument[] = [];
        if (!userCompanyDocuments || userCompanyDocuments.length === 0) {
            // Se l'atleta non ha documenti o è null, gli mancano tutti i documenti obbligatori
            missingCompanyDocuments = [...listOfMandatoryDocuments];
        } else {
            listOfMandatoryDocuments.forEach((mandatoryDocument: CompanyDocument) => {
                if (!userCompanyDocuments!.some((userDocument: CompanyDocument) => mandatoryDocument.id === userDocument.id)) {
                    missingCompanyDocuments.push(mandatoryDocument);
                }
            });
        }

        return missingCompanyDocuments;
    }

    async getUserDocuments(userId: string): Promise<{ success: boolean; data: CompanyDocument[] }> {
        try {
            const response: any = await this.http.get(environment.apiUrl + `/userCompanyDocuments/${userId}`, {
                headers: this.getRequestHeaders(),
            }).toPromise();

            if (response?.success) {
                return {success: true, data: response.data ?? []};
            }

            return {success: false, data: []};
        } catch (e) {
            console.error('error getUserDocuments: ', e);
            return {success: false, data: []};
        }
    }

    async deleteUserDocuments(userId: string, documentId: string): Promise<{ success: boolean }> {
        try {
            const response: any = await this.http.delete(
                environment.apiUrl + `/userCompanyDocuments/${userId}/${documentId}`,
                {headers: this.getRequestHeaders()}
            ).toPromise();

            return {success: !!response?.success};
        } catch (e) {
            console.error('error deleteUserDocuments: ', e);
            return {success: false};
        }
    }




}
